// src/pages/deals/components/DealAttachments.tsx
import React, { useState } from 'react';
import { Button, Upload, Table, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { DealAttachmentDto } from 'api/models';
import { DealsApi } from 'api/api';
import { formatDate, formatDateTime } from 'utility/utility';
import openNotificationWithIcon from 'utility/notification';
import FeatherIcon from 'feather-icons-react';

interface DealAttachmentsProps {
  dealId: number;
  attachments: DealAttachmentDto[];
  onAttachmentsChange: () => void;
}

const dealsApi = new DealsApi();

const DealAttachments: React.FC<DealAttachmentsProps> = ({ dealId, attachments, onAttachmentsChange }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await dealsApi.apiDealsIdAttachmentsPost({
        id: dealId,
        dealId: dealId,
        file: file,
      });
      openNotificationWithIcon('success', t('deals:file-uploaded-successfully', 'File uploaded successfully'));
      onAttachmentsChange();
    } catch (error) {
      openNotificationWithIcon('error', t('deals:file-upload-error', 'Failed to upload file'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId: string) => {
    try {
      await dealsApi.apiDealsIdAttachmentsAttachmentIdDelete({
        id: dealId,
        attachmentId: attachmentId,
      });
      openNotificationWithIcon('success', t('deals:file-deleted-successfully', 'File deleted successfully'));
      onAttachmentsChange();
    } catch (error) {
      openNotificationWithIcon('error', t('deals:file-deletion-failed', 'File deletion failed'));
    }
  };

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      const response = await dealsApi.apiDealsIdAttachmentsAttachmentIdGet(
        {
          id: dealId,
          attachmentId: attachmentId,
        },
        { responseType: 'blob' },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      openNotificationWithIcon('success', t('deals:file-downloaded-successfully', 'File downloaded successfully'));
    } catch (error) {
      console.error(error);
      openNotificationWithIcon('error', t('deals:file-download-failed', 'Failed to download file'));
    }
  };

  const columns = [
    {
      title: t('deals:file-name', 'File Name'),
      dataIndex: 'fileName',
      key: 'fileName',
    },
    {
      title: t('deals:file-type', 'File Type'),
      dataIndex: 'fileType',
      key: 'fileType',
    },
    {
      title: t('deals:file-size', 'File Size'),
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size: number) => `${(size / 1024).toFixed(2)} KB`,
    },
    {
      title: t('deals:uploaded-at', 'Uploaded At'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDateTime(date),
    },
    {
      title: t('common:actions', 'Actions'),
      key: 'actions',
      render: (_: any, record: DealAttachmentDto) => (
        <div className="table-actions">
          <Button type="link" onClick={() => handleDownload(record.id!, record.fileName!)}>
            <FeatherIcon icon="download" size={16} />
          </Button>
          <Popconfirm
            title={t('deals:confirm-delete-file', 'Are you sure you want to delete this file?')}
            onConfirm={() => handleDelete(record.id!)}
            okText={t('common:yes', 'Yes')}
            cancelText={t('common:no', 'No')}
          >
            <Button type="link" danger>
              <FeatherIcon icon="trash" size={16} />
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Upload
        beforeUpload={file => {
          handleUpload(file);
          return false;
        }}
        showUploadList={false}
      >
        <Button loading={uploading} style={{ marginBottom: 16 }}>
          <FeatherIcon icon="upload" size={16} />
          {t('deals:upload-file', 'Upload File')}
        </Button>
      </Upload>

      <Table columns={columns} dataSource={attachments} rowKey="id" pagination={false} />
    </div>
  );
};

export default DealAttachments;
